import React from 'react'
import classNames from 'classnames'
import propTypes from 'prop-types'
import { FaTimes, FaEye, FaImage, FaMinus, FaPenAlt, FaVideo, FaVolumeUp } from 'react-icons/fa'
import { useClient } from 'jsonapi-react'

import { ActionButton } from 'layouts/basic'
import Modal from 'components/Modal'
import { InputFile } from 'components/Forms/Input'
import AlbumItemThumb from './AlbumItemThumb'
import Loader from 'components/Loader'


export default function AlbumItem(props) {
  const [edit, setEdit] = React.useState(false)
  const [isSettingAsThumbnail, setSettingAsThumbnail] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [uploadingFile, setUploadingFile] = React.useState({
    file: false,
    audio: false,
  })
  const client = useClient()

  const uploadFile = async (e, itemId, type) => {
    const file = e.currentTarget.files[0]
    let formData = new FormData()
    formData.append('album_item_type', type)
    formData.append('file', file)
    setUploadingFile({
      ...uploadingFile,
      [type]: true,
    })
    await client.mutate(
      [
        'albums',
        props.album.id,
        'items',
        itemId,
        'upload'
      ],
      false,
      {
        body: formData,
        headers: {
          'Content-Type': undefined,
        },
      }
    )
    setUploadingFile({
      ...uploadingFile,
      [type]: false,
    })
  }

  const deleteItem = async () => {
    setIsDeleting(true)
    await client.delete(['albums', props.album.id, 'items', props.id])
  }

  const setAsThumbnail = async () => {
    setSettingAsThumbnail(true)
    await client.mutate(['albums', props.album.id, 'items', props.id, 'as_thumbnail'])
    setSettingAsThumbnail(false)
  }

  const actionButtons = []

  if (props.permissions.delete) {
    actionButtons.push(
      <ActionButton
        loading={isDeleting}
        key={`delete_button_${props.id}`}
        theme='danger'
        onClick={deleteItem}
      >
        <FaMinus />
      </ActionButton>
    )
  }
  if (props.permissions.edit) {
    actionButtons.push(
      <ActionButton
        theme='info'
        key={`edit_button_${props.id}`}
        onClick={() => setEdit(true)}
      >
        <FaPenAlt />
      </ActionButton>
    )
  }
  if (props.album.permissions.edit) {
    actionButtons.push(
      <ActionButton
        key={`change_thumbnail_${props.id}`}
        loading={isSettingAsThumbnail}
        active={props.asAlbumThumbnail}
        theme={props.asAlbumThumbnail ? 'success' : 'disabled'}
        onClick={setAsThumbnail}
      >
        <FaImage />
      </ActionButton>
    )
  }
  if ( ! props.permissions.edit) {
    actionButtons.push(
      <ActionButton
        key={`view_item_${props.id}`}
        onClick={() => setEdit(true)}
      >
        <FaEye />
      </ActionButton>
    )
  }

  return (
    <div
      onClick={props.onClick}
      className={classNames([
        'relative',
        'group',
        'aspect-w-10 aspect-h-10',
        props.className,
      ])}
    >
      <Modal
        active={!!edit}
        onClose={() => setEdit(false)}
        className='p-5 sm:p-10'
      >
        <Modal.Content>
          <div className='prose-sm sm:prose-lg'>
            <h2>
              Edit album item{' '}
            </h2>
            <div className='mb-10'>
              <h3>
                Video or Image File{' '}
                <Loader
                  className='inline-block text-primary-600'
                  active={uploadingFile.file}
                />
              </h3>
              {
                props.permissions.uploadFile &&
                <InputFile
                  inputProps={{
                    accept: 'image/jpg, image/png, image/jpeg, video/*',
                  }}
                  onChange={e => uploadFile(e, props.id, 'file')}
                  filename={props?.file?.title}
                  loading={uploadingFile.file}
                />
              }
              {
                props?.file?.type === 'Video' &&
                <video
                  disableRemotePlayback
                  controls
                  src={props.file?.small || props.file?.medium || props.file?.file}
                />
              }
              {
                props?.file?.type === 'Image' &&
                <img
                  className='rounded-lg shadow-lg mx-auto'
                  src={props.file?.small || props.file?.medium || props.file?.file}
                  alt={props.album.title}
                />
              }
            </div>
            <div className='mb-10 mt-20'>
              <h3>
                Audio File{' '}
                <Loader
                  className='inline-block text-primary-600'
                  active={uploadingFile.audio}
                />
              </h3>
              {
                props.permissions.uploadFile &&
                <InputFile
                  inputProps={{
                    accept: 'audio/*',
                  }}
                  onChange={e => uploadFile(e, props.id, 'audio')}
                  filename={props?.audio?.title}
                  loading={uploadingFile.audio}
                />
              }
              {
                props?.audio &&
                <audio controls src={props.audio.file} />
              }
            </div>
            {
              (uploadingFile.file || uploadingFile.audio) &&
              <div className='mb-10 mt-20'>
                <div className='h-auto flex items-center text-xl font-bold float-right cursor-pointer text-red-500' onClick={() => setEdit(false)}>
                  <FaTimes className='mr-1'/>Close window and continue
                </div>
                <div className='float-right'>(Don't worry, the process continues working in background)</div>
              </div>
            }
          </div>
        </Modal.Content>
      </Modal>
      <div>
        <div
          className='
            absolute z-10 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-30
            transition-all
            lg:opacity-0 lg:group-hover:opacity-100
            space-x-3
            flex items-center justify-center
          '
        >
          { actionButtons }
        </div>
        <div className='
          absolute top-0 z-10 left-0 right-0
          flex space-x-1 justify-center items-end
          p-3
        '>
          {
            props.asAlbumThumbnail &&
            <div className='rounded-full bg-success-600 shadow-lg p-3 text-white'>
              <FaImage />
            </div>
          }
        </div>
        <AlbumItemThumb
          videoThumbnail
          file={props.file}
        />
        <div className='
          absolute z-10 bottom-0 left-0 right-0
          flex space-x-1 justify-center items-end
          p-3
        '>
          {
            props.file?.type === 'Video' &&
            <div className='rounded-full bg-disabled-600 shadow-lg p-3 text-white'>
              <FaVideo />
            </div>
          }
          {
            props.audio?.type === 'Audio' &&
            <div className='rounded-full bg-disabled-600 shadow-lg p-3 text-white'>
              <FaVolumeUp />
            </div>
          }
          {
            props.file?.type === 'Image' &&
            <div className='rounded-full bg-disabled-600 shadow-lg p-3 text-white'>
              <FaImage />
            </div>
          }
        </div>
      </div>
    </div>
  )
}

AlbumItem.propTypes = {
  className: propTypes.string,
  thumbnail: propTypes.string,
  permissions: propTypes.object,
  file: propTypes.object,
  audio: propTypes.object,
  album: propTypes.object,
  id: propTypes.string.isRequired,
  asAlbumThumbnail: propTypes.bool,
  videoThumbnail: propTypes.bool,
}
